<template lang="pug">
v-container.pa-0.invoice-tab(:class="{'invoice-tab--mobile': isPullRefreshEnabled}" v-on="pullRefreshListeners")
  v-snackbar(v-model="showError" color="error")
    span {{ errorMessage }}
    v-btn(dark text @click="showError = false")
      v-icon fal fa-times

  v-snackbar(v-model="showBatchNotice" :color="batchNoticeColor")
    span {{ batchNoticeMessage }}
    v-btn(dark text @click="showBatchNotice = false")
      v-icon fal fa-times

  div.pull-refresh(v-if="isPullRefreshEnabled && (isPullGestureActive || isPullRefreshing)")
    v-icon.mr-2(small :color="isPullRefreshing ? 'primary' : 'grey darken-1'") {{ pullIcon }}
    span {{ pullLabel }}

  v-row.mb-3(dense)
    v-col(cols="6" sm="3")
      v-card.invoice-summary-card(outlined)
        v-skeleton-loader.invoice-summary-card__body.pa-3(v-if="fetchInProgress" type="text, text")
        v-card-text.invoice-summary-card__body.py-2.px-3(v-else)
          div.text-caption {{ $t('boarderDetailDialog.invoiceSummaryTotalLabel') }}
          div.text-subtitle-1.font-weight-bold {{ formattedTotalInvoiced }} €
    v-col(cols="6" sm="3")
      v-card.invoice-summary-card(outlined)
        v-skeleton-loader.invoice-summary-card__body.pa-3(v-if="fetchInProgress" type="text, text")
        v-card-text.invoice-summary-card__body.py-2.px-3(v-else)
          div.text-caption {{ $t('boarderDetailDialog.invoiceSummaryPaidLabel') }}
          div.text-subtitle-1.font-weight-bold {{ formattedTotalPaid }} €
    v-col(cols="6" sm="3")
      v-card.invoice-summary-card(outlined)
        v-skeleton-loader.invoice-summary-card__body.pa-3(v-if="fetchInProgress" type="text, text")
        v-card-text.invoice-summary-card__body.py-2.px-3(v-else)
          div.text-caption {{ $t('boarderDetailDialog.invoiceSummaryOutstandingLabel') }}
          div.text-subtitle-1.font-weight-bold {{ formattedTotalOutstanding }} €
    v-col(cols="6" sm="3")
      v-card.invoice-summary-card(outlined)
        v-skeleton-loader.invoice-summary-card__body.pa-3(v-if="fetchInProgress" type="text, text")
        v-card-text.invoice-summary-card__body.py-2.px-3(v-else)
          div.text-caption {{ $t('boarderDetailDialog.invoiceSummaryCountLabel') }}
          div.text-subtitle-1.font-weight-bold {{ invoiceList.length }}

  v-card.mb-3(outlined v-if="!isPullRefreshEnabled && invoiceList.length > 0")
    v-toolbar(dense flat)
      v-toolbar-items.invoice-batch-toolbar__items
        v-tooltip(top)
          template(v-slot:activator="{ on, attrs }")
            v-btn(
              icon
              small
              :color="isSelectionMode ? 'primary' : 'grey darken-1'"
              :disabled="batchEmailInProgress || (!isSelectionMode && !canEnterSelectionMode)"
              :aria-label="$t('boarderDetailDialog.selectInvoicesButtonLabel').toString()"
              v-bind="attrs"
              v-on="on"
              @click="toggleSelectionMode"
            )
              v-icon {{ isSelectionMode ? 'fas fa-check-square' : 'far fa-check-square' }}
          span {{ $t('boarderDetailDialog.selectInvoicesButtonLabel') }}
        v-tooltip(top)
          template(v-slot:activator="{ on, attrs }")
            v-btn(
              icon
              small
              :loading="isRefreshingInvoices"
              :disabled="fetchInProgress || batchEmailInProgress"
              :aria-label="$t('boarderDetailDialog.refreshInvoicesButtonLabel').toString()"
              v-bind="attrs"
              v-on="on"
              @click="refreshInvoices"
            )
              v-icon fas fa-sync
          span {{ $t('boarderDetailDialog.refreshInvoicesButtonLabel') }}
      v-spacer
      v-toolbar-items(v-if="isSelectionMode")
        v-btn(
          small
          text
          color="primary"
          class="ml-2"
          :loading="batchEmailInProgress"
          :disabled="!hasSelectedInvoices || batchEmailInProgress"
          @click="onSendBatchEmailClick"
        )
          v-icon(left small) far fa-envelope
          span {{ sendInvoiceBatchButtonLabel }}

  v-data-table.invoice-table(
    v-if="!isPullRefreshEnabled"
    :class="{'invoice-table--selecting': isSelectionMode}"
    :headers="headers"
    :items="sortedInvoiceList"
    item-key="id"
    disable-pagination
    hide-default-footer
    dense
    :loading="fetchInProgress"
    :loading-text="$t('billingPage.loadingGridDataText').toString()"
    @click:row="onInvoiceRowClick"
  )
    template(v-slot:item.selection="{ item }")
      div.selection-cell(@click.stop)
        v-checkbox.ma-0.pa-0(
          :input-value="isInvoiceSelected(item)"
          hide-details
          dense
          color="primary"
          on-icon="fas fa-check-square"
          off-icon="far fa-square"
          @change="toggleInvoiceSelection(item, $event)"
        )
    template(v-slot:item.paymentStatus="{ item }")
      v-icon(:color="item.paymentStatusColor" small) {{ item.paymentStatusIcon }}
    template(v-slot:item.name="{ item }")
      div
        div.text-body-2.font-weight-medium.pt-1 {{ item.name }}
        div.text-caption.grey--text.text--darken-1 {{ item.formattedPeriodRange }}
    template(v-slot:item.issueDate="{ item }")
      span {{ formatDate(item.issueDate) }}
    template(v-slot:item.dueDate="{ item }")
      span {{ formatDate(item.dueDate) }}
    template(v-slot:item.formattedPriceTotal="{ item }")
      div.text-right {{ item.formattedPriceTotal }} €
    template(v-slot:item.formattedAmountAlreadyPaid="{ item }")
      div.text-right {{ item.formattedAmountAlreadyPaid }} €
    template(v-slot:item.formattedOutstandingAmountToPay="{ item }")
      div.text-right {{ item.formattedOutstandingAmountToPay }} €

  v-row(v-else-if="isPullRefreshEnabled && fetchInProgress" dense)
    v-col(
      v-for="idx in 3"
      :key="idx"
      cols="12"
      sm="6"
      xl="6"
    )
      v-card.invoice-card(outlined)
        v-card-text.py-2.px-3
          v-row(no-gutters align="start")
            v-col(cols="1" class="pr-2")
              v-skeleton-loader(type="avatar" width="24")
            v-col(cols="11")
              v-skeleton-loader.mb-2(type="text")
              v-skeleton-loader(type="text")
          v-row.mt-1(dense)
            v-col(cols="4" class="text-center")
              v-skeleton-loader(type="text")
            v-col(cols="4" class="text-center")
              v-skeleton-loader(type="text")
            v-col(cols="4" class="text-center")
              v-skeleton-loader(type="text")

  v-row(v-else-if="isPullRefreshEnabled && invoiceList.length > 0" dense)
    v-col(
      v-for="invoice in sortedInvoiceList"
      :key="invoice.id"
      cols="12"
      sm="6"
      xl="6"
    )
      v-card.invoice-card(outlined)
        v-card-text.py-2.px-3
          v-row(no-gutters align="start")
            v-col(cols="1" class="pr-2")
              v-icon.mt-1(:color="invoice.paymentStatusColor" small) {{ invoice.paymentStatusIcon }}
            v-col(cols="11" class="pr-2")
              div.text-body-1.font-weight-medium.text-truncate {{ invoice.name }}
              div.text-caption.grey--text.text--darken-1.text-truncate {{ invoice.formattedPeriodRange }}
          v-row.mt-1(dense)
            v-col(cols="4" class="text-center")
              div.text-caption.text-nowrap {{ $t('boarderDetailDialog.invoiceMealPriceLabel') }}
              div.text-body-1.font-weight-bold {{ invoice.formattedPriceTotal }} €
            v-col(cols="4" class="text-center")
              div.text-caption.text-nowrap {{ $t('boarderDetailDialog.invoicePaidColumnLabel') }}
              div.text-body-1.font-weight-bold {{ invoice.formattedAmountAlreadyPaid }} €
            v-col(cols="4" class="text-center")
              div.text-caption.text-nowrap {{ $t('boarderDetailDialog.invoiceOutstandingLabel') }}
              div.text-body-1.font-weight-bold {{ invoice.formattedOutstandingAmountToPay }} €

  v-row(v-else-if="hasLoadedInvoices && invoiceList.length === 0")
    v-col(cols="12").title.text-center {{ $t('boarderDetailDialog.noInvoicesText') }}
</template>

<script lang="ts">
import Vue from 'vue'
import {Component, Prop, Watch} from 'vue-property-decorator'
import {BoarderResponse} from '@/types/service-response'
import {Invoice} from '@/model/billing/Invoice'
import axios from '@/plugins/axios'
import {formatIntPrice} from '@/services/formatter'
import {DataTableHeader} from 'vuetify'
import moment from 'moment'

@Component
export default class BoarderInvoiceTab extends Vue {
  @Prop({ type: Object, required: true }) readonly boarder!: BoarderResponse
  @Prop({ type: Boolean, default: false }) readonly isActive!: boolean

  private invoiceList: Invoice[] = []
  private selectedInvoices: Invoice[] = []
  private fetchInProgress = false
  private batchEmailInProgress = false
  private isRefreshingInvoices = false
  private isPullRefreshing = false
  private isPullGestureActive = false
  private pullStartY = 0
  private pullCurrentY = 0
  private pullThreshold = 88
  private showError = false
  private errorMessage = ''
  private showBatchNotice = false
  private batchNoticeMessage = ''
  private batchNoticeColor = 'success'
  private hasLoadedInvoices = false
  private loadedBoarderId: number | null = null
  private isSelectionMode = false

  get formattedTotalInvoiced(): string {
    return formatIntPrice(this.invoiceList.reduce((sum, invoice) => sum + invoice.priceTotal, 0))
  }

  get formattedTotalPaid(): string {
    return formatIntPrice(this.invoiceList.reduce((sum, invoice) => sum + invoice.amountAlreadyPaid, 0))
  }

  get formattedTotalOutstanding(): string {
    return formatIntPrice(this.invoiceList.reduce((sum, invoice) => sum + invoice.outstandingAmountToPay, 0))
  }

  get hasSelectedInvoices(): boolean {
    return this.selectedInvoices.length > 0
  }

  get canEnterSelectionMode(): boolean {
    return !this.fetchInProgress && !this.batchEmailInProgress && this.invoiceList.length > 0
  }

  get sendInvoiceBatchButtonLabel(): string {
    return this.$t('boarderDetailDialog.sendInvoiceBatchButtonLabelWithCount', {
      count: this.selectedInvoices.length
    }).toString()
  }

  get headers(): DataTableHeader[] {
    const headers: DataTableHeader[] = [
      { text: '', value: 'selection', sortable: false, width: 56 },
      { text: '', value: 'paymentStatus', sortable: false, width: 40 },
      { text: this.$t('boarderDetailDialog.invoiceNameColumnLabel').toString(), value: 'name' },
      { text: this.$t('boarderDetailDialog.invoiceIssueDateColumnLabel').toString(), value: 'issueDate' },
      { text: this.$t('boarderDetailDialog.invoiceDueDateColumnLabel').toString(), value: 'dueDate' },
      { text: this.$t('boarderDetailDialog.invoiceMealPriceLabel').toString(), value: 'formattedPriceTotal' },
      { text: this.$t('boarderDetailDialog.invoicePaidColumnLabel').toString(), value: 'formattedAmountAlreadyPaid' },
      { text: this.$t('boarderDetailDialog.invoiceOutstandingLabel').toString(), value: 'formattedOutstandingAmountToPay' },
    ]

    if (!this.isSelectionMode) {
      return headers.filter((header) => header.value !== 'selection')
    }

    return headers
  }

  get sortedInvoiceList(): Invoice[] {
    return [...this.invoiceList].sort((left: Invoice, right: Invoice) => {
      return moment(right.issueDate).valueOf() - moment(left.issueDate).valueOf()
    })
  }

  get isPullRefreshEnabled(): boolean {
    return this.$vuetify.breakpoint.smAndDown
  }

  get pullRefreshListeners(): Record<string, (event: TouchEvent) => void> {
    if (!this.isPullRefreshEnabled) {
      return {}
    }

    return {
      touchstart: this.onTouchStart,
      touchmove: this.onTouchMove,
      touchend: this.onTouchEnd,
      touchcancel: this.resetPullState,
    }
  }

  get pullDistance(): number {
    return Math.max(0, this.pullCurrentY - this.pullStartY)
  }

  get pullIcon(): string {
    return this.isPullRefreshing || this.pullDistance >= this.pullThreshold ? 'fas fa-sync' : 'fas fa-arrow-down'
  }

  get pullLabel(): string {
    if (this.isPullRefreshing) {
      return this.$t('boarderDetailDialog.refreshingLabel').toString()
    }

    if (this.pullDistance >= this.pullThreshold) {
      return this.$t('boarderDetailDialog.releaseToRefreshLabel').toString()
    }

    return this.$t('boarderDetailDialog.pullToRefreshLabel').toString()
  }

  formatDate(value: Date | string): string {
    return moment(value).format('D.M.YYYY')
  }

  getBoarderIri(): string {
    return `/api/food-ordering/boarder/${this.boarder.id}`
  }

  getSelectedInvoiceIris(): string[] {
    return this.selectedInvoices.map((invoice: Invoice) => invoice.iri)
  }

  isInvoiceSelected(invoice: Invoice): boolean {
    return this.selectedInvoices.some((selectedInvoice: Invoice) => selectedInvoice.id === invoice.id)
  }

  toggleInvoiceSelection(invoice: Invoice, selected: boolean): void {
    if (selected) {
      if (!this.isInvoiceSelected(invoice)) {
        this.selectedInvoices = [...this.selectedInvoices, invoice]
      }

      return
    }

    this.selectedInvoices = this.selectedInvoices.filter((selectedInvoice: Invoice) => selectedInvoice.id !== invoice.id)
  }

  onInvoiceRowClick(invoice: Invoice): void {
    if (!this.isSelectionMode || this.batchEmailInProgress) {
      return
    }

    this.toggleInvoiceSelection(invoice, !this.isInvoiceSelected(invoice))
  }

  showBatchFeedback(message: string, color: string): void {
    this.batchNoticeMessage = message
    this.batchNoticeColor = color
    this.showBatchNotice = true
  }

  getScrollContainer(): HTMLElement | null {
    if (!(this.$el instanceof HTMLElement)) {
      return null
    }

    return this.$el.closest('.dialog-content') as HTMLElement | null
  }

  async fetchInvoices(preserveBatchState = false): Promise<void> {
    this.fetchInProgress = true

    if (!preserveBatchState) {
      this.resetBatchState()
    }

    try {
      const response = await axios.get('/api/billing/invoice', {
        params: {
          boarder: this.boarder.id
        }
      })

      this.invoiceList = Invoice.createListFromResponse(response.data)
      this.hasLoadedInvoices = true
      this.loadedBoarderId = this.boarder.id

      if (this.invoiceList.length === 0) {
        this.resetBatchState()
      }
    } catch (e) {
      this.showError = true
      this.errorMessage = this.$t('errors.fetchDataError').toString()
    } finally {
      this.fetchInProgress = false
    }
  }

  async onSendBatchEmailClick(): Promise<void> {
    if (!this.hasSelectedInvoices || this.batchEmailInProgress) {
      return
    }

    this.batchEmailInProgress = true

    try {
      await axios.post('/api/billing/invoice/batch-email', {
        boarder: this.getBoarderIri(),
        invoices: this.getSelectedInvoiceIris()
      })

      this.exitSelectionMode()
      this.showBatchFeedback(this.$t('boarderDetailDialog.batchInvoiceSendSuccessMessage').toString(), 'success')
    } catch (error) {
      this.showBatchFeedback(this.getBatchErrorMessage(error), 'error')
    } finally {
      this.batchEmailInProgress = false
    }
  }

  getBatchErrorMessage(error: any): string {
    return error?.response?.data?.['hydra:description']
      || error?.response?.data?.detail
      || this.$t('errors.sendDataError').toString()
  }

  async refreshInvoices(): Promise<void> {
    if (this.fetchInProgress || this.batchEmailInProgress || this.isRefreshingInvoices) {
      return
    }

    this.isRefreshingInvoices = true
    this.isPullRefreshing = true

    try {
      await this.fetchInvoices(true)
      this.resetBatchSelection()
    } finally {
      this.isRefreshingInvoices = false
      this.isPullRefreshing = false
      this.resetPullState()
    }
  }

  onTouchStart(event: TouchEvent): void {
    if (!this.isPullRefreshEnabled || !this.isActive || this.fetchInProgress || this.isPullRefreshing || event.touches.length !== 1) {
      return
    }

    const scrollContainer = this.getScrollContainer()
    if (scrollContainer && scrollContainer.scrollTop > 0) {
      return
    }

    this.pullStartY = event.touches[0].clientY
    this.pullCurrentY = this.pullStartY
    this.isPullGestureActive = false
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isPullRefreshEnabled || !this.isActive || this.fetchInProgress || this.isPullRefreshing || !this.pullStartY || event.touches.length !== 1) {
      return
    }

    const scrollContainer = this.getScrollContainer()
    if (scrollContainer && scrollContainer.scrollTop > 0) {
      this.resetPullState()
      return
    }

    const currentY = event.touches[0].clientY
    const delta = currentY - this.pullStartY

    if (delta <= 0) {
      this.resetPullState()
      return
    }

    this.pullCurrentY = currentY
    this.isPullGestureActive = true
  }

  async onTouchEnd(): Promise<void> {
    if (!this.isPullRefreshEnabled || !this.isPullGestureActive) {
      this.resetPullState()
      return
    }

    const shouldRefresh = this.pullDistance >= this.pullThreshold
    this.resetPullState()

    if (shouldRefresh) {
      await this.refreshInvoices()
    }
  }

  resetPullState(): void {
    this.pullStartY = 0
    this.pullCurrentY = 0
    this.isPullGestureActive = false
  }

  resetBatchSelection(): void {
    this.selectedInvoices = []
  }

  resetBatchState(): void {
    this.isSelectionMode = false
    this.resetBatchSelection()
  }

  enterSelectionMode(): void {
    if (!this.canEnterSelectionMode) {
      return
    }

    this.isSelectionMode = true
  }

  exitSelectionMode(): void {
    this.resetBatchState()
  }

  toggleSelectionMode(): void {
    if (this.isSelectionMode) {
      this.exitSelectionMode()
      return
    }

    this.enterSelectionMode()
  }

  @Watch('isActive', { immediate: true })
  onActiveChange(): void {
    if (!this.isActive) {
      this.resetBatchState()
      return
    }

    if (this.boarder && this.loadedBoarderId !== this.boarder.id) {
      this.hasLoadedInvoices = false
      this.fetchInvoices()
    }
  }

  @Watch('boarder.id')
  onBoarderIdChange(): void {
    this.resetBatchState()

    if (this.isActive && this.boarder && this.loadedBoarderId !== this.boarder.id) {
      this.hasLoadedInvoices = false
      this.fetchInvoices()
    }
  }
}
</script>

<style lang="stylus" scoped>
.invoice-card
  height 100%

.invoice-summary-card
  min-height 65px
  height 100%

.invoice-summary-card__body
  min-height 65px
  display flex
  flex-direction column
  justify-content center

.pull-refresh
  display flex
  align-items center
  justify-content center
  padding 4px 0 8px
  font-size 12px
  color rgba(0, 0, 0, .6)

.invoice-tab
  touch-action pan-y

.invoice-tab--mobile
  touch-action pan-y

.invoice-batch-toolbar
  display flex
  align-items center
  min-height 48px

.invoice-batch-toolbar__items
  display flex
  align-items center
  gap 4px

.invoice-table--selecting
  /deep/ tbody tr
    cursor pointer

</style>
